'use client';

import React from 'react';

import { Box, BoxProps } from '../box';
import { Text } from '../text';

export interface AvatarProps extends BoxProps {
  /** Image source URL */
  src?: string;
  /** Name for initials fallback */
  name?: string;
  /** Avatar size */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  /** Shape of avatar */
  shape?: 'circle' | 'square';
  /** Status indicator */
  status?: 'online' | 'offline' | 'away' | 'busy';
  /** Border color */
  borderColor?: string;
  /** Border width */
  borderWidth?: number;
  /** Custom fallback content */
  children?: React.ReactNode;
}

const sizeMap = {
  xs: { size: 6, fontSize: 'xs' },
  sm: { size: 8, fontSize: 'sm' },
  md: { size: 10, fontSize: 'md' },
  lg: { size: 12, fontSize: 'lg' },
  xl: { size: 16, fontSize: 'xl' },
  '2xl': { size: 20, fontSize: '2xl' },
};

const statusColors = {
  online: 'green.500',
  offline: 'gray.500',
  away: 'yellow.500',
  busy: 'red.500',
};

/**
 * Avatar component - displays user avatars with image or initials fallback
 * Supports status indicators and various sizes
 */
export const Avatar: React.FC<AvatarProps> = ({
  src,
  name,
  size = 'md',
  shape = 'circle',
  status,
  borderColor,
  borderWidth,
  children,
  backgroundColor = 'bg.muted',
  color = 'fg.default',
  ...boxProps
}) => {
  const { size: avatarSize, fontSize } = sizeMap[size];
  const borderRadius = shape === 'circle' ? 'full' : 'md';

  // Get initials from name
  const getInitials = (name: string) => {
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  const [imageError, setImageError] = React.useState(false);

  return (
    <Box
      position="relative"
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      width={avatarSize}
      height={avatarSize}
      borderRadius={borderRadius}
      backgroundColor={backgroundColor}
      color={color}
      overflow="hidden"
      flexShrink={0}
      border={borderWidth ? `${borderWidth}px solid` : undefined}
      borderColor={borderColor}
      {...boxProps}
    >
      {/* Image */}
      {src && !imageError && (
        <img
          src={src}
          alt={name}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
          onError={() => setImageError(true)}
        />
      )}

      {/* Initials fallback */}
      {(!src || imageError) && !children && name && (
        <Text fontSize={fontSize} fontWeight="medium" lineHeight="1">
          {getInitials(name)}
        </Text>
      )}

      {/* Custom content */}
      {(!src || imageError) && children}

      {/* Status indicator */}
      {status && (
        <Box
          position="absolute"
          bottom={0}
          right={0}
          width={avatarSize / 4}
          height={avatarSize / 4}
          borderRadius="full"
          backgroundColor={statusColors[status]}
          border="2px solid"
          borderColor="bg.surface"
        />
      )}
    </Box>
  );
};

export default Avatar;
